// hooks/useAsthmaAssessment.tsx
import { useState, useEffect } from 'react';
import { SeverityLevel } from '../utils/asthmaTypes';
import {
  calculateAsthmaScore,
  mapScoreToSeverity,
} from '../utils/asthmaScoring';

export function useAsthmaAssessment() {
  // === Inputs ===
  const [age, setAge] = useState('');
  const [respiratoryRate, setRespiratoryRate] = useState('');
  const [oxygenSaturation, setOxygenSaturation] = useState('');
  const [oxygenSupport, setOxygenSupport] = useState(false);
  const [auscultation, setAuscultation] = useState('');
  const [retractions, setRetractions] = useState('');
  const [dyspnea, setDyspnea] = useState('');

  // === State ===
  const [score, setScore] = useState(0);
  const [severity, setSeverity] = useState('');
  const [activeSeverity, setActiveSeverity] = useState<SeverityLevel | null>(null);
  const [treatmentRecommendation, setTreatmentRecommendation] = useState('');
  const [initialAssessmentTime, setInitialAssessmentTime] = useState<string | null>(null);

  // === Locking Mechanism ===
  const [isAssessmentLocked, setIsAssessmentLocked] = useState(false);

  // Calculation Effect
  useEffect(() => {
    // === Main Fix ===
    // If the system is locked (we are in treatment), do not recalculate anything!
    // Rely only on what comes from memory or rechecks.
    if (isAssessmentLocked) return;

    // Prevent running on empty data
    if (!age && !respiratoryRate && !oxygenSaturation) return;

    const totalScore = calculateAsthmaScore({
      age,
      respiratoryRate,
      oxygenSaturation,
      oxygenSupport,
      auscultation,
      retractions,
      dyspnea,
    });

    setScore(totalScore);

    const {
      severityLabel,
      severityLevel,
      recommendation,
    } = mapScoreToSeverity(totalScore);

    setSeverity(severityLabel);
    setActiveSeverity(severityLevel);
    setTreatmentRecommendation(recommendation);

  }, [
    age, respiratoryRate, oxygenSaturation, oxygenSupport,
    auscultation, retractions, dyspnea, isAssessmentLocked
  ]);

  const stampInitialAssessmentTime = () => {
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    setInitialAssessmentTime(`${hh}:${mm}`);
  };

  // Recheck overrides the lock and updates directly
  const applyRecheckResult = (newScore: number) => {
    setScore(newScore);
    const { severityLabel, severityLevel, recommendation } = mapScoreToSeverity(newScore);

    setSeverity(severityLabel);
    setActiveSeverity(severityLevel);
    setTreatmentRecommendation(recommendation);
  };

  return {
    age, setAge,
    respiratoryRate, setRespiratoryRate,
    oxygenSaturation, setOxygenSaturation,
    oxygenSupport, setOxygenSupport,
    auscultation, setAuscultation,
    retractions, setRetractions,
    dyspnea, setDyspnea,

    score, setScore,
    severity, setSeverity,
    activeSeverity, setActiveSeverity,
    treatmentRecommendation, setTreatmentRecommendation,
    initialAssessmentTime, setInitialAssessmentTime,

    isAssessmentLocked, setIsAssessmentLocked,

    stampInitialAssessmentTime,
    applyRecheckResult,
  };
}